import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown, CreditCard, RefreshCw, Search,
} from "lucide-react";
import { apiGet, apiPost } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

function normalizeText(v) { return String(v || "").trim(); }
function normalizeSeat(v)  { return String(v || "").replace(/[^\d]/g, "").slice(0, 6); }
function fmt(v) {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return "-";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(t));
}

export default function AdminTenantBilling() {
  const { tenantId }    = useParams();
  const location        = useLocation();
  const navigate        = useNavigate();

  /* ── tenant meta (from nav state or fetched) ── */
  const [tenantName, setTenantName] = useState(location.state?.tenantName || "");
  const [tenantMeta, setTenantMeta] = useState(null); // full tenant object

  /* ── billing data ── */
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [invoices,      setInvoices]      = useState([]);
  const [plans,         setPlans]         = useState([]);
  const [loading,       setLoading]       = useState(true);

  /* ── active sub-tab ── */
  const [subTab, setSubTab] = useState("subscriptions");

  /* ── search per sub-tab ── */
  const [subSearch, setSubSearch] = useState("");

  /* ── grant-tier form ── */
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    planId: "", billingCycle: "monthly",
    paymentMode: "admin_granted", seatCount: "", notes: "",
  });

  /* ═══════ load ═══════ */
  const load = async () => {
    setLoading(true);
    try {
      const [subsRes, payRes, invRes, planRes, tenantsRes] = await Promise.all([
        apiGet(`/api/admin/billing/subscriptions?tenantId=${encodeURIComponent(tenantId)}&limit=200`),
        apiGet(`/api/admin/billing/payments?tenantId=${encodeURIComponent(tenantId)}&limit=200`),
        apiGet(`/api/admin/billing/invoices?tenantId=${encodeURIComponent(tenantId)}&limit=200`),
        apiGet("/api/admin/plan-pricing"),
        apiGet("/api/admin/tenants"),
      ]);

      const [subs, pays, invs, planPayload, allTenants] = await Promise.all([
        subsRes.json().catch(() => null),
        payRes.json().catch(() => null),
        invRes.json().catch(() => null),
        planRes.json().catch(() => null),
        tenantsRes.json().catch(() => []),
      ]);

      if (!subsRes.ok) throw new Error(subs?.message  || "Failed to fetch subscriptions");
      if (!payRes.ok)  throw new Error(pays?.message   || "Failed to fetch payments");
      if (!invRes.ok)  throw new Error(invs?.message   || "Failed to fetch invoices");
      if (!planRes.ok) throw new Error(planPayload?.message || "Failed to fetch plans");

      const catalog = Array.isArray(planPayload?.catalog) ? planPayload.catalog : [];
      setSubscriptions(Array.isArray(subs?.subscriptions) ? subs.subscriptions : []);
      setPayments(Array.isArray(pays?.payments)           ? pays.payments       : []);
      setInvoices(Array.isArray(invs?.invoices)           ? invs.invoices       : []);
      setPlans(catalog);
      setForm(p => ({ ...p, planId: p.planId || String(catalog[0]?.id || "") }));

      const tenant = Array.isArray(allTenants)
        ? allTenants.find(t => t.id === tenantId) || null
        : null;
      if (tenant) {
        setTenantMeta(tenant);
        if (!tenantName) setTenantName(tenant.name || "");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [tenantId]);

  /* ═══════ grant tier ═══════ */
  const grantTier = async (e) => {
    e.preventDefault();
    if (saving || !form.planId) { toast.error("Select a plan"); return; }
    const payload = {
      planId: form.planId, billingCycle: form.billingCycle,
      paymentMode: form.paymentMode || "admin_granted",
      notes: normalizeText(form.notes),
    };
    const seats = Number.parseInt(form.seatCount, 10);
    if (Number.isFinite(seats) && seats > 0) payload.seatCount = seats;
    setSaving(true);
    try {
      const res = await apiPost(
        `/api/admin/billing/tenants/${encodeURIComponent(tenantId)}/grant-tier`,
        payload,
      );
      const d = await res.json().catch(() => null);
      if (!res.ok || !d?.success) { toast.error(d?.message || "Failed to grant tier"); return; }
      toast.success("Tier granted");
      void load();
    } catch { toast.error("Failed to grant tier"); }
    finally { setSaving(false); }
  };

  /* ═══════ navigate to record detail ═══════ */
  const goDetail = (type, r) =>
    navigate(`/admin/tenants/${tenantId}/billing/${type}/${r.id}`, {
      state: { record: r, tenantName, tenantId },
    });

  /* ═══════ filtered lists ═══════ */
  const filteredSubs = useMemo(() => {
    const q = normalizeText(subSearch).toLowerCase();
    if (!q) return subscriptions;
    return subscriptions.filter(r =>
      [r?.planName, r?.planId, r?.status, r?.billingCycle].some(v =>
        normalizeText(v).toLowerCase().includes(q)
      )
    );
  }, [subscriptions, subSearch]);

  const filteredPays = useMemo(() => {
    const q = normalizeText(subSearch).toLowerCase();
    if (!q) return payments;
    return payments.filter(r =>
      [r?.planName, r?.planId, r?.status, r?.paymentMode].some(v =>
        normalizeText(v).toLowerCase().includes(q)
      )
    );
  }, [payments, subSearch]);

  const filteredInvs = useMemo(() => {
    const q = normalizeText(subSearch).toLowerCase();
    if (!q) return invoices;
    return invoices.filter(r =>
      [r?.invoiceNumber, r?.id, r?.status].some(v =>
        normalizeText(v).toLowerCase().includes(q)
      )
    );
  }, [invoices, subSearch]);

  /* ═══════ summary counts ═══════ */
  const activeSubs  = subscriptions.filter(r => r?.status === "active").length;
  const paidPayments = payments.filter(r => r?.status === "paid").length;
  const totalRevenue = payments
    .filter(r => r?.status === "paid")
    .reduce((sum, r) => sum + Number(r?.totalAmount || 0), 0);

  /* ════════════════════════════════════════
     Render
     ════════════════════════════════════════ */
  return (
    <div className="admin-workspace">

      {/* Breadcrumb */}
      <div className="atb-breadcrumb">
        <button className="atb-back-btn" onClick={() => navigate("/admin/tenants")}>
          All Tenants
        </button>
        <ChevronDown size={12} className="atb-sep" />
        <button className="atb-back-btn" onClick={() => navigate(`/admin/tenants/${tenantId}`)}>
          {tenantName || "Tenant"}
        </button>
        <ChevronDown size={12} className="atb-sep" />
        <span className="atb-breadcrumb-current">Billing</span>
      </div>

      {/* Page header */}
      <div className="atb-page-header">
        <div>
          <h1 className="atb-page-title">
            <CreditCard size={20} />
            Billing
            {tenantName && <span className="atb-title-tenant">— {tenantName}</span>}
          </h1>
          {tenantMeta && (
            <div className="atb-page-meta">
              <span>{tenantMeta.ownerEmail || ""}</span>
              <span className={`admin-status ${tenantMeta.isActive ? "is-active" : "is-disabled"}`} style={{ fontSize: "0.75rem" }}>
                {tenantMeta.isActive ? "Active" : "Disabled"}
              </span>
              {tenantMeta.activePlanName && (
                <span className="admin-chip">{tenantMeta.activePlanName}</span>
              )}
            </div>
          )}
        </div>
        <button className="gc-icon-btn" data-tip="Refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Summary pills */}
      {!loading && (
        <div className="atb-pills">
          <div className="atb-pill">
            <span>Active Subscriptions</span>
            <strong>{activeSubs}</strong>
          </div>
          <div className="atb-pill">
            <span>Paid Payments</span>
            <strong>{paidPayments}</strong>
          </div>
          <div className="atb-pill atb-pill--highlight">
            <span>Total Collected</span>
            <strong>₹{totalRevenue.toLocaleString("en-IN")}</strong>
          </div>
          <div className="atb-pill">
            <span>Invoices</span>
            <strong>{invoices.length}</strong>
          </div>
        </div>
      )}

      {/* Grant / Change Tier form */}
      <div className="admin-panel atb-grant-panel">
        <div className="admin-panel-head">
          <h3 className="admin-panel-title">Grant / Change Tier</h3>
        </div>
        <form onSubmit={grantTier}>
          <div className="atb-grant-grid">
            <div className="admin-form-field">
              <label className="admin-form-label">Plan</label>
              <select
                className="admin-input"
                value={form.planId}
                onChange={e => setForm(p => ({ ...p, planId: e.target.value }))}
                disabled={saving}
              >
                {plans.map(pl => (
                  <option key={pl.id} value={pl.id}>{pl.name || pl.id}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-field">
              <label className="admin-form-label">Billing Cycle</label>
              <select
                className="admin-input"
                value={form.billingCycle}
                onChange={e => setForm(p => ({ ...p, billingCycle: e.target.value }))}
                disabled={saving}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="one_time">One Time</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label className="admin-form-label">Payment Mode</label>
              <select
                className="admin-input"
                value={form.paymentMode}
                onChange={e => setForm(p => ({ ...p, paymentMode: e.target.value }))}
                disabled={saving}
              >
                <option value="admin_granted">Admin Grant (No Payment)</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="razorpay_collected_by_admin">Razorpay by Admin</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label className="admin-form-label">Seat Count</label>
              <input
                className="admin-input"
                placeholder="e.g. 10"
                value={form.seatCount}
                onChange={e => setForm(p => ({ ...p, seatCount: normalizeSeat(e.target.value) }))}
                disabled={saving}
              />
            </div>
            <div className="admin-form-field atb-notes-field">
              <label className="admin-form-label">Admin Note</label>
              <input
                className="admin-input"
                placeholder="Optional note"
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value.slice(0, 500) }))}
                disabled={saving}
              />
            </div>
          </div>
          <div className="admin-actions" style={{ marginTop: 14 }}>
            <button type="submit" className="admin-btn" disabled={saving || loading}>
              {saving ? "Granting…" : "Grant Tier"}
            </button>
          </div>
        </form>
      </div>

      {/* Records section */}
      <div className="atb-records-section">
        {/* Sub-tab bar */}
        <div className="atb-subtab-bar">
          {[
            { id: "subscriptions", label: "Subscriptions" },
            { id: "payments",      label: "Payments" },
            { id: "invoices",      label: "Invoices" },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              className={`atb-subtab${subTab === t.id ? " is-active" : ""}`}
              onClick={() => { setSubTab(t.id); setSubSearch(""); }}
            >
              {t.label}
            </button>
          ))}

          {/* search aligned to right */}
          <label className="admin-search atb-subtab-search">
            <Search size={13} />
            <input
              type="search"
              placeholder={`Search ${subTab}…`}
              value={subSearch}
              onChange={e => setSubSearch(e.target.value)}
            />
          </label>
        </div>

        {/* Tables */}
        {loading ? (
          <div className="admin-empty-state">Loading billing records…</div>
        ) : subTab === "subscriptions" ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Billing Cycle</th>
                  <th>Seats</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubs.length === 0 ? (
                  <tr><td colSpan={5} className="atb-empty-row">No subscriptions found.</td></tr>
                ) : filteredSubs.map(r => (
                  <tr key={r.id} className="atb-row-link" onClick={() => goDetail("subscription", r)}>
                    <td>
                      <div className="admin-cell-title">{r.planName || r.planId || "-"}</div>
                    </td>
                    <td>
                      <span className={`admin-status ${r.status === "active" ? "is-active" : r.status === "expired" ? "is-disabled" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td>{r.billingCycle || "-"}</td>
                    <td>{r.seatCount ?? "-"}</td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>
                        {r.expiresAt ? fmt(r.expiresAt) : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : subTab === "payments" ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredPays.length === 0 ? (
                  <tr><td colSpan={5} className="atb-empty-row">No payment records.</td></tr>
                ) : filteredPays.map(r => (
                  <tr key={r.id} className="atb-row-link" onClick={() => goDetail("payment", r)}>
                    <td>
                      <div className="admin-cell-title">{r.planName || r.planId || "-"}</div>
                    </td>
                    <td>
                      <div className="admin-cell-title">
                        ₹{Number(r.totalAmount || 0).toLocaleString("en-IN")}
                      </div>
                    </td>
                    <td>{r.paymentMode || "-"}</td>
                    <td>
                      <span className={`admin-status ${r.status === "paid" ? "is-active" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>
                        {r.createdAt ? fmt(r.createdAt) : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvs.length === 0 ? (
                  <tr><td colSpan={4} className="atb-empty-row">No invoices found.</td></tr>
                ) : filteredInvs.map(r => (
                  <tr key={r.id} className="atb-row-link" onClick={() => goDetail("invoice", r)}>
                    <td>
                      <div className="admin-cell-title admin-monospace">
                        {r.invoiceNumber || r.id || "-"}
                      </div>
                    </td>
                    <td>
                      <span className={`admin-status ${r.status === "paid" ? "is-active" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-cell-title">
                        ₹{Number(r.total || 0).toLocaleString("en-IN")}
                      </div>
                    </td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>
                        {r.createdAt ? fmt(r.createdAt) : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        /* breadcrumb */
        .atb-breadcrumb {
          display: flex; align-items: center; gap: 6px;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .atb-back-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 13px; font-weight: 500; color: var(--as-text-2);
          background: none; border: none; padding: 0; cursor: pointer;
          transition: color .12s;
        }
        .atb-back-btn:hover { color: var(--as-primary); }
        .atb-sep {
          transform: rotate(-90deg); color: var(--as-text-3); flex-shrink: 0;
        }
        .atb-breadcrumb-current {
          font-size: 13px; color: var(--as-text); font-weight: 600;
        }

        /* page header */
        .atb-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; flex-wrap: wrap; margin-bottom: 14px;
        }
        .atb-page-title {
          display: flex; align-items: center; gap: 10px;
          font-size: 1.35rem; font-weight: 700; color: var(--as-text); margin: 0 0 6px;
        }
        .atb-title-tenant {
          font-size: 1rem; font-weight: 400; color: var(--as-text-2);
        }
        .atb-page-meta {
          display: flex; align-items: center; gap: 10px;
          font-size: 13px; color: var(--as-text-2); flex-wrap: wrap;
        }

        /* summary pills */
        .atb-pills {
          display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap;
        }
        .atb-pill {
          display: flex; flex-direction: column; gap: 2px;
          padding: 10px 18px;
          border: 1px solid var(--as-border); border-radius: 6px;
          background: var(--as-surface);
          min-width: 120px;
        }
        .atb-pill span { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--as-text-2); }
        .atb-pill strong { font-size: 1.25rem; font-weight: 700; color: var(--as-text); }
        .atb-pill--highlight { border-color: rgba(0,120,212,.25); background: rgba(0,120,212,.05); }
        .atb-pill--highlight strong { color: var(--as-primary); }

        /* grant panel */
        .atb-grant-panel { margin-bottom: 20px; }
        .atb-grant-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }
        .atb-notes-field { grid-column: span 2; }
        @media (max-width: 600px) { .atb-notes-field { grid-column: span 1; } }

        /* form helpers */
        .admin-form-field { display: flex; flex-direction: column; gap: 4px; }
        .admin-form-label { font-size: 12px; font-weight: 600; color: var(--as-text); }

        /* records section */
        .atb-records-section { margin-top: 4px; }

        /* sub-tab bar */
        .atb-subtab-bar {
          display: flex; align-items: center;
          gap: 2px; border-bottom: 2px solid var(--as-border);
          margin-bottom: 0;
        }
        .atb-subtab {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 9px 16px; font-size: 13px; font-weight: 500;
          color: var(--as-text-2); background: none; border: none;
          border-bottom: 2px solid transparent; margin-bottom: -2px;
          cursor: pointer; white-space: nowrap;
          transition: color .12s, border-color .12s;
        }
        .atb-subtab:hover { color: var(--as-text); }
        .atb-subtab.is-active { color: var(--as-primary); border-bottom-color: var(--as-primary); font-weight: 600; }
.atb-subtab-search { margin-left: auto; max-width: 260px; }

        /* table rows */
        .atb-row-link { cursor: pointer; }
        .atb-row-link:hover td { background: var(--as-bg) !important; }
        .atb-empty-row { text-align: center; color: var(--as-text-2); padding: 24px !important; }
      `}</style>
    </div>
  );
}
